import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { UserPermissionsResponse } from '@/types/permissions';
import { api, getToken } from '@/services/api';

interface PermissionContextValue {
  permissions: UserPermissionsResponse | null;
  loading: boolean;
  refresh: () => void;
}

const PermissionContext = createContext<PermissionContextValue>({
  permissions: null,
  loading: true,
  refresh: () => {},
});

export function PermissionProvider({ children }: { children: ReactNode }) {
  const [permissions, setPermissions] = useState<UserPermissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const token = getToken();
    if (!token) {
      setPermissions(null);
      setLoading(false);
      return;
    }
    try {
      const data = await api<UserPermissionsResponse>('/permissions/me');
      setPermissions(data);
    } catch {
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-background)]">
        <div className="relative mb-8 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--color-primary)] text-2xl font-bold text-white shadow-lg">J</div>
          <div className="absolute inset-0 h-14 w-14 animate-ping rounded-xl bg-[var(--color-primary)] opacity-20" />
        </div>
        <div className="text-2xl font-bold tracking-tight text-[var(--color-foreground)]">Jani</div>
        <div className="mt-4 text-sm text-[var(--color-muted-foreground)]">Loading permissions...</div>
      </div>
    );
  }

  return (
    <PermissionContext.Provider value={{ permissions, loading, refresh: load }}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  return useContext(PermissionContext);
}
